<script setup lang="ts">
import PlanetHeaderVideo from '@/components/Elements/Videos/PlanetHeaderVideo.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import FormInput from '@/components/Interface/Modal/Forms/FormInput.vue'
import ToggleSwitch from '@/components/Elements/ToggleSwitch/ToggleSwitch.vue'
import { useBattleSimulatorStore } from '@/stores/battleSimulatorStore'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import RangeSlider from '@/components/Elements/Forms/RangeSlider.vue'

const { t } = useI18n()
const battleSimulatorStore = useBattleSimulatorStore()

const isPopulationInvalid = computed(() => battleSimulatorStore.planetPopulation < 1)
const isEnergyInvalid = computed(() => battleSimulatorStore.planetEnergy < 0)
const isVshipsInvalid = computed(() => battleSimulatorStore.planetVships < 0)
const isShieldsInvalid = computed(() => battleSimulatorStore.planetShields < 0)
</script>

<template>
  <div class="mt-6">
    <div class="planet-header rounded-t relative overflow-hidden">
      <PlanetHeaderVideo :type="3" class="absolute inset-0 w-full h-full object-cover" />
      <div class="absolute inset-0 header-fade"></div>
      <span class="text-xl text-white relative z-10 p-2 block">{{ t('command.battleSimulator.defendingPlanet.title') }}</span>
    </div>
    <div class="bg-metal-800/40 p-3 rounded-b">
      <div class="flex items-center gap-4">
        <FormInput
          v-model="battleSimulatorStore.planetPopulation"
          type="number"
          min="1"
          :input-class="`design text-center ${isPopulationInvalid ? 'ring-1 ring-red-500' : ''}`"
          class="w-24"
        >
          <template #icon>
            <GenericIcon type="person" :tooltip="t('command.battleSimulator.defendingPlanet.population')" />
          </template>
        </FormInput>
        <div class="flex-1 flex items-center gap-2">
          <GenericIcon type="soldier" :tooltip="t('command.battleSimulator.defendingPlanet.troops')" />
          <RangeSlider
            v-model="battleSimulatorStore.troopPercentage"
            :min="0"
            :max="100"
            class="flex-1"
          />
          <span class="text-metal-300 w-12 text-right">{{ battleSimulatorStore.troopPercentage }}%</span>
        </div>
        <div class="text-metal-300 w-28 text-right">
          <span class="font-semibold">{{ battleSimulatorStore.planetTroops }}</span> {{ t('command.battleSimulator.defendingPlanet.troopsLabel') }}
        </div>
      </div>
      <div class="flex items-center gap-4 mt-3">
        <FormInput
          v-model="battleSimulatorStore.planetEnergy"
          type="number"
          min="0"
          :input-class="`design text-center ${isEnergyInvalid ? 'ring-1 ring-red-500' : ''}`"
          class="w-24"
        >
          <template #icon>
            <GenericIcon type="energy_storage" :tooltip="t('command.battleSimulator.defendingPlanet.maxEnergy')" />
          </template>
        </FormInput>
        <div class="flex-1 flex items-center gap-2">
          <GenericIcon type="energy_storage" :tooltip="t('command.battleSimulator.defendingPlanet.shieldCapacity')" />
          <RangeSlider
            v-model="battleSimulatorStore.shieldPercentage"
            :min="0"
            :max="100"
            class="flex-1"
          />
          <span class="text-metal-300 w-12 text-right">{{ battleSimulatorStore.shieldPercentage }}%</span>
        </div>
        <div class="text-metal-300 w-28 text-right">
          <span class="font-semibold">{{ battleSimulatorStore.currentShieldEnergy }}</span> {{ t('command.battleSimulator.defendingPlanet.shields') }}
        </div>
      </div>
      <div class="flex items-center gap-4 mt-3">
        <FormInput
          v-model="battleSimulatorStore.planetVships"
          type="number"
          min="0"
          :input-class="`design text-center ${isVshipsInvalid ? 'ring-1 ring-red-500' : ''}`"
          class="w-24"
        >
          <template #icon>
            <GenericIcon type="attack_against_ships" :tooltip="t('command.battleSimulator.defendingPlanet.virtualShips')" />
          </template>
        </FormInput>
        <FormInput
          v-model="battleSimulatorStore.planetShields"
          type="number"
          min="0"
          :input-class="`design text-center ${isShieldsInvalid ? 'ring-1 ring-red-500' : ''}`"
          class="w-24"
        >
          <template #icon>
            <GenericIcon type="shields" :tooltip="t('command.shipDesigns.tooltips.shields')" />
          </template>
        </FormInput>
        <div class="flex items-center gap-2 text-metal-300">
          <ToggleSwitch v-model="battleSimulatorStore.planetFocusFire" class="mb-2" />
          <span>{{ t('command.battleSimulator.defendingPlanet.focusFireMinister') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.planet-header {
  height: 8rem;
}

.header-fade {
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.6) 0%, transparent 50%);
}
</style>
